import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MoneyInput } from "@/components/ui/money-input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Check, History, X } from "lucide-react";

const fmt = (n) => new Intl.NumberFormat("en-TZ", { maximumFractionDigits: 2 }).format(Number(n) || 0);
const today = () => new Date().toISOString().slice(0, 10);
const STATUS_LABEL = { unpaid_invoice: "unpaid", partially_paid: "partially paid", paid: "paid" };

// "50,000 → 40,000 · 2026-09-01 → 2026-09-02" -- only the parts that change.
export function describeChange(c) {
  if (c.void) return `Void ${fmt(c.before.amount)} (${c.before.method || "no account"}, ${c.before.date || "undated"})`;
  const parts = [];
  if (c.before.amount !== c.after.amount) parts.push(`Amount ${fmt(c.before.amount)} → ${fmt(c.after.amount)}`);
  if (c.before.date !== c.after.date) parts.push(`Date ${c.before.date || "—"} → ${c.after.date}`);
  if (c.before.method !== c.after.method) parts.push(`Account ${c.before.method || "—"} → ${c.after.method}`);
  return parts.length ? parts.join(" · ") : `Restore missing sale ${c.sale_no}`;
}

// Raising a request changes nothing -- it records what's wrong and why, for
// a SuperAdmin to approve (see backend/routes/payment_corrections_routes.py).
export function CorrectionForm({ invoiceId, payment, onDone, onCancel }) {
  const [voiding, setVoiding] = useState(false);
  const [amount, setAmount] = useState(String(payment.amount));
  const [date, setDate] = useState(payment.date || "");
  const [method, setMethod] = useState(payment.method || "");
  const [reason, setReason] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [saving, setSaving] = useState(false);
  const formRef = useRef(null);

  // The invoice dialog scrolls, and Payment History sits at its bottom, so
  // the form opens out of sight -- bring it into view and focus it.
  useEffect(() => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    formRef.current?.querySelector("input")?.focus({ preventScroll: true });
  }, [payment.id]);

  useEffect(() => {
    api.get("/chart-of-accounts/", { params: { limit: 100 } })
      .then(({ data }) => setAccounts(data.items || []))
      .catch(() => {});
  }, []);

  const submit = async () => {
    if (reason.trim().length < 5) { toast.error("Explain what went wrong"); return; }
    if (!voiding && !(Number(amount) > 0)) { toast.error("Amount must be greater than zero — to remove the payment, void it"); return; }
    setSaving(true);
    try {
      await api.post(`/invoices/${invoiceId}/payments/${payment.id}/corrections`, voiding
        ? { void: true, reason }
        : { amount: Number(amount), date, method, reason });
      toast.success("Correction requested — waiting for SuperAdmin approval");
      onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div ref={formRef} className="space-y-3 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3" data-testid="correction-form">
      <p className="text-xs text-muted-foreground">
        Correcting the {fmt(payment.amount)} payment{payment.sale_no ? ` (${payment.sale_no})` : ""}.
        Nothing changes until a SuperAdmin approves; then the invoice, its sale and the account balance are all updated together.
      </p>
      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <Checkbox checked={voiding} onCheckedChange={(v) => setVoiding(v === true)} data-testid="correction-void" />
        Void this payment entirely (recorded on the wrong invoice, or never received)
      </label>
      {!voiding && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <div className="space-y-1">
            <Label className="text-xs">Correct amount</Label>
            <MoneyInput value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="correction-amount" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Correct date</Label>
            <Input type="date" max={today()} value={date} onChange={(e) => setDate(e.target.value)} data-testid="correction-date" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Correct account</Label>
            <SearchableSelect
              value={method}
              onValueChange={setMethod}
              options={accounts.map((a) => ({ value: a.name, label: a.name }))}
              placeholder="Select account…"
              testId="correction-account"
            />
          </div>
        </div>
      )}
      <div className="space-y-1">
        <Label className="text-xs">Reason<span className="text-rose-500 ml-1">*</span></Label>
        <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Typed 50,000 instead of 500,000 — see NMB slip 4471" data-testid="correction-reason" />
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onCancel}>Cancel</Button>
        <Button size="sm" onClick={submit} disabled={saving} className="kn-gradient-bg text-white border-0" data-testid="submit-correction">
          {saving ? "Sending…" : "Request correction"}
        </Button>
      </div>
    </div>
  );
}

function Impact({ impact }) {
  if (!impact) return null;
  return (
    <ul className="text-xs text-muted-foreground list-disc pl-4 space-y-0.5">
      <li>
        Invoice paid {fmt(impact.amount_paid_before)} → <b>{fmt(impact.amount_paid_after)}</b> ({STATUS_LABEL[impact.status_after] || impact.status_after})
      </li>
      {impact.account_moves.length === 0
        ? <li>No account balance changes</li>
        : impact.account_moves.map((m, i) => (
          <li key={i}>{m.account}: {m.amount > 0 ? "+" : "−"}{fmt(Math.abs(m.amount))} (adjusting entry dated today)</li>
        ))}
    </ul>
  );
}

// Pending requests (approve/reject for SuperAdmin) plus the permanent record
// of every decided one.
export function CorrectionList({ corrections, isSuperAdmin, onDecided }) {
  const [busy, setBusy] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const pending = corrections.filter((c) => c.status === "pending");
  const decided = corrections.filter((c) => c.status !== "pending");

  const decide = async (c, action) => {
    let body = {};
    if (action === "reject") {
      const note = window.prompt("Why is this correction rejected? (optional)");
      if (note === null) return;
      body = { note };
    }
    setBusy(c.id);
    try {
      const { data } = await api.post(`/payment-corrections/${c.id}/${action}`, body);
      toast.success(action === "approve" ? "Correction applied" : "Correction rejected");
      onDecided(data.invoice);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(null);
    }
  };

  if (!corrections.length) return null;
  return (
    <div className="space-y-2">
      {pending.map((c) => (
        <div key={c.id} className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 space-y-1.5" data-testid={`pending-correction-${c.id}`}>
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <p className="text-sm font-medium">{describeChange(c)}</p>
              <p className="text-xs text-muted-foreground">
                “{c.reason}” — {c.requested_by}, {c.requested_at?.slice(0, 10)}
              </p>
            </div>
            {isSuperAdmin ? (
              <div className="flex gap-1.5">
                <Button size="sm" variant="outline" disabled={busy === c.id} onClick={() => decide(c, "reject")} data-testid="reject-correction">
                  <X className="w-3.5 h-3.5 mr-1" /> Reject
                </Button>
                <Button size="sm" disabled={busy === c.id} onClick={() => decide(c, "approve")} className="kn-gradient-bg text-white border-0" data-testid="approve-correction">
                  <Check className="w-3.5 h-3.5 mr-1" /> Approve
                </Button>
              </div>
            ) : (
              <span className="text-xs text-amber-700 dark:text-amber-400">Waiting for SuperAdmin approval</span>
            )}
          </div>
          <Impact impact={c.impact} />
        </div>
      ))}
      {decided.length > 0 && (
        <div>
          <button type="button" onClick={() => setShowHistory((v) => !v)}
            className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="toggle-correction-history">
            <History className="w-3 h-3" /> {showHistory ? "Hide" : "Show"} correction history ({decided.length})
          </button>
          {showHistory && (
            <ul className="mt-1.5 space-y-1 text-xs">
              {decided.map((c) => (
                <li key={c.id} className="rounded border border-border px-2 py-1.5">
                  <span className={c.status === "approved" ? "text-emerald-600" : "text-rose-600"}>{c.status}</span>
                  {" · "}{describeChange(c)} — “{c.reason}”
                  <span className="text-muted-foreground"> · asked by {c.requested_by}, decided by {c.decided_by} on {c.decided_at?.slice(0, 10)}
                    {c.decision_note ? ` (“${c.decision_note}”)` : ""}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
